import {
  apiWithAdminToken,
  apiWithMerchantToken,
  apiWithMerchantTokenV2,
  apiWithRefreshToken,
  imageApiWithAdminToken,
  imageApiWithMerchantToken,
} from '@lib/api';
import {
  apply,
  withAdminTokenAccepted,
  withMerchantTokenAccepted,
  withRefreshTokenAccepted,
  withTokenError,
  withTokenRejected,
  withUserTokenAccepted,
} from '@lib/interceptors';
import axios, { AxiosInstance } from 'axios';

/**
 * v2 API
 */

const API_URL_V2 = process.env.REACT_APP_API_URL_V2;
const API_IMG_URL = process.env.REACT_APP_API_IMG_URL;

const createInstance = (url?: string, timeout?: number) =>
  axios.create({ baseURL: `${API_URL_V2}${url}`, timeout: timeout || 30000 });
const createImageInstance = (url?: string, timeout?: number) =>
  axios.create({ baseURL: `${API_IMG_URL}${url}`, timeout: timeout || 0 });

const auth = createInstance(`/auth`);
const seat = createInstance(`/seat`);
const user = createInstance(`/user`);
const ticket = createInstance(`/ticket`);

const authWithRefreshToken = createInstance('/auth');
export const authWithUserToken = createInstance('/auth');
export const authWithMerchantToken = createInstance('/auth');
const authWithAdminToken = createInstance('/auth');
export const userWithoutToken = createInstance('/user');

export const userWithAdminToken = createInstance('/admin/user');
export const couponWithAdminToken = createInstance('/admin/coupon');
export const versionWithAdminToken = createInstance('/admin/version');
export const appWithAdminToken = createInstance('/admin/app');
export const appImageWithAdminToken = createImageInstance('/v2/backoffice/admin/app');
export const communityWithAdminToken = createInstance('/admin/community');
export const merchantWithAdminToken = createInstance('/admin/merchant');
export const paymentWithAdminToken = createInstance('/admin/payment');
export const ticketWithAdminToken = createInstance('/admin/ticket');
export const franchiseWithAdminToken = createInstance('/admin/franchise');
export const salesWithAdminToken = createInstance('/admin/sales');
export const pgConfigWithAdminToken = createInstance('/admin/pg-config');
export const subscriptionWithAdminToken = createInstance('/admin/subscription');
export const messageWithAdminToken = createInstance('/admin/message');
export const messageProviderWithAdminToken = createInstance('/admin/message-provider');
export const promoCampaignWithAdminToken = createInstance('/admin/promo-campaign');
export const promoCampaignImageWithAdminToken = createImageInstance(
  '/v2/backoffice/admin/promo-campaign',
);

export const userWithUserToken = createInstance('/user');
export const merchantWithMerchantToken = createInstance('/merchant');
export const merchantImageWithMerchantToken = createImageInstance('/v2/backoffice/merchant');

export const seatWithMerchantToken = createInstance('/seat');
export const seatImageWithMerchantToken = createImageInstance('/v2/backoffice/seat');
export const userWithMerchantToken = createInstance('/user');
export const paymentWithMerchantToken = createInstance('/payment');
export const couponWithMerchantToken = createInstance('/coupon');
export const merchantPgWithMerchantToken = createInstance('/merchant-pg');
export const messageWithMerchantToken = createImageInstance('/v2/backoffice/message');
export const communityWithMerchantToken = createInstance('/community');
export const appWithMerchantToken = createInstance('/app');
export const appImageWithMerchantToken = createImageInstance('/v2/backoffice/app');
export const subscriptionWithMerchantToken = createInstance('/subscription');
export const messageAutoChargeWithMerchantToken = createInstance('/messageAutoCharge');
export const biostar2WithMerchantToken = createInstance('/biostar2');
export const mileageWithMerchantToken = createInstance('/mileage');
export const logWithMerchantToken = createInstance('/log');
export const chatWithoutToken = createInstance('/chat');
export const chatWithMerchantToken = createInstance('/chat');
export const chatWithAdminToken = createInstance('/chat');
export const socketWithMerchantToken = createInstance('/socket');
export const socketWithAdminToken = createInstance('/socket');

const instancesWithRefreshToken: AxiosInstance[] = [authWithRefreshToken, apiWithRefreshToken];
const instancesWithMerchantToken: AxiosInstance[] = [
  seat,
  user,
  ticket,
  apiWithMerchantToken,
  apiWithMerchantTokenV2,
  imageApiWithMerchantToken,
  userWithMerchantToken,
  paymentWithMerchantToken,
  seatWithMerchantToken,
  merchantWithMerchantToken,
  merchantPgWithMerchantToken,
  couponWithMerchantToken,
  messageWithMerchantToken,
  seatImageWithMerchantToken,
  authWithMerchantToken,
  merchantImageWithMerchantToken,
  communityWithMerchantToken,
  mileageWithMerchantToken,
  appWithMerchantToken,
  appImageWithMerchantToken,
  subscriptionWithMerchantToken,
  messageAutoChargeWithMerchantToken,
  biostar2WithMerchantToken,
  logWithMerchantToken,
  chatWithMerchantToken,
  socketWithMerchantToken,
];
const instancesWithUserToken: AxiosInstance[] = [userWithUserToken, authWithUserToken];
const instancesWithAdminToken: AxiosInstance[] = [
  authWithAdminToken,
  apiWithAdminToken,
  imageApiWithAdminToken,
  couponWithAdminToken,
  versionWithAdminToken,
  communityWithAdminToken,
  merchantWithAdminToken,
  userWithAdminToken,
  paymentWithAdminToken,
  ticketWithAdminToken,
  appWithAdminToken,
  appImageWithAdminToken,
  franchiseWithAdminToken,
  salesWithAdminToken,
  pgConfigWithAdminToken,
  subscriptionWithAdminToken,
  messageWithAdminToken,
  messageProviderWithAdminToken,
  promoCampaignWithAdminToken,
  promoCampaignImageWithAdminToken,
  chatWithAdminToken,
  socketWithAdminToken,
];

instancesWithRefreshToken.forEach((instance) => {
  instance.interceptors.request.use(withRefreshTokenAccepted, withTokenError);
  instance.interceptors.response.use(apply, withTokenRejected);
});
instancesWithMerchantToken.forEach((instance) => {
  instance.interceptors.request.use(withMerchantTokenAccepted, withTokenError);
  instance.interceptors.response.use(apply, withTokenRejected);
});
instancesWithUserToken.forEach((instance) => {
  instance.interceptors.request.use(withUserTokenAccepted, withTokenError);
  instance.interceptors.response.use(apply, withTokenRejected);
});
instancesWithAdminToken.forEach((instance) => {
  instance.interceptors.request.use(withAdminTokenAccepted, withTokenError);
  instance.interceptors.response.use(apply, withTokenRejected);
});

export { auth, authWithAdminToken, authWithRefreshToken, seat, ticket, user };

/**
 * 회원 조회
 * @param {*} merchantId
 * @param {*} data : { phoneNum?, name?, gender?, userType?, parentAlertConsent?, usageStatus? }
 *   - 문자 발송 대상 멀티필터 (LETMEUP-344): gender('0'/'1'), userType('0'/'1'),
 *     parentAlertConsent('0'/'1'), usageStatus('using'/'unused'/'expired')
 */
export const getUserList = (merchantId: number, data: any = {}) => {
  const params = new URLSearchParams();
  if (data.phoneNum) params.append('phoneNum', data.phoneNum);
  if (data.name) params.append('name', data.name);
  if (data.gender !== undefined && data.gender !== '')
    params.append('gender', String(data.gender));
  if (data.userType !== undefined && data.userType !== '')
    params.append('userType', String(data.userType));
  if (data.parentAlertConsent !== undefined && data.parentAlertConsent !== '')
    params.append('parentAlertConsent', String(data.parentAlertConsent));
  if (data.usageStatus) params.append('usageStatus', data.usageStatus);

  const qs = params.toString();
  return user.get(`/userList/${merchantId}${qs ? `?${qs}` : ''}`);
};

/**
 * 회원 정보 조회
 * @param {*} merchantId
 * @param {*} userId
 */
export const userTicketList = (merchantId: number, userId: number, type: any) =>
  ticket.get(`/userTicketList/${merchantId}/${userId}/${type}`);

/**
 * 해당 좌석에 입장
 * @param {*} data : { merchantId: string; userId: string; ticketId: string; seatId: string; }
 * @returns
 */
export const forceEnter = (data: any) => ticket.patch(`/forceEnter`, data);
